import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { ProductSearchResponse, SearchQueryParams } from '@woolworthsnz/search';
import {
	ApiService,
	AppSettingsService,
	DatalayerService,
	FeatureService,
	FlagKey,
	FlagService,
	HttpArgs,
	InfiniteLoadMoreExperimentVariants,
	isDefined,
	LocalStorageService,
	LoggingService,
	SearchTarget,
	sentenceCase,
} from '@woolworthsnz/styleguide';
import {
	ContextResponse,
	DepartmentDasFacetsResponse,
	FilterCriteria,
	ProductResponse,
	SearchResponse,
} from '@woolworthsnz/trader-api';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { SearchStoreService } from './search-store.service';

interface GoogleAnalyticsParams {
	source: string;
	searchterm?: string;
}

const dynamicProductGroups = {
	onecard: 'onecardspecial',
	multibuy: 'multibuy',
	greatprice: 'greatprice',
	lowprice: 'lowprice',
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'member-prices': 'memberprices',
};

export enum PageSize {
	TwentyFour = 24,
	FourtyEight = 48,
}

const isRequiredTarget = (targets: Array<SearchTarget>, target: SearchTarget): boolean => targets.includes(target);
const isBrowseOrSearch = (target: SearchTarget): boolean =>
	isRequiredTarget([SearchTarget.Browse, SearchTarget.Search], target);

@Injectable({
	providedIn: 'root',
})
export class SearchService {
	constructor(
		private apiService: ApiService,
		private appSettingsService: AppSettingsService,
		private datalayerService: DatalayerService,
		private featureService: FeatureService,
		private flagService: FlagService,
		private localStorageService: LocalStorageService,
		private loggingService: LoggingService,
		private route: ActivatedRoute,
		private router: Router,
		private searchStoreService: SearchStoreService
	) {}

	get searchEndpoint(): string {
		return this.appSettingsService.getEndpoint('search');
	}

	get departmentsEndpoint(): string {
		return this.appSettingsService.getEndpoint('departments');
	}

	fetchBrowseDepartments(): Observable<DepartmentDasFacetsResponse[]> {
		return this.apiService.get(this.departmentsEndpoint);
	}

	fetchProducts = (params: SearchQueryParams, routeUrl = ''): Observable<ProductSearchResponse> => {
		this.loggingService.trackEvent('UI:Search:Any:fetchProducts', params);

		const args: HttpArgs = {
			params,
		};

		return this.apiService.get(this.searchEndpoint, args).pipe(
			map(
				(r) =>
					<ProductSearchResponse>{
						...r,
						routeUrl,
						params,
					}
			),
			tap((response: ProductSearchResponse) => {
				if (response.action?.type === 'redirect' && response.action.url) {
					const url = new URL(response.action.url);
					if (url) {
						this.loggingService.trackEvent('UI:Search:Response:redirect', { params: response.params, url });

						const googleAnalyticsParams: GoogleAnalyticsParams = { source: 'search_internal_redirect' };
						if (response.params.search) {
							googleAnalyticsParams.searchterm = response.params.search;
						}

						this.router.navigate([url.pathname], { queryParams: googleAnalyticsParams, replaceUrl: true });
					}
				}

				if (!response.products?.items?.length) {
					this.handleFailure('noResults', null);
				}
			}),
			tap((response: ProductSearchResponse) => {
				// We need to wait for next tick to load route information
				setTimeout(() => this.trackSearchPageView(response), 0);
			}),
			catchError((error) => {
				this.handleSearchError(error);
				return of(error);
			})
		);
	};

	private handleSearchError(error: any): void {
		this.searchStoreService.setState({
			products: {
				items: [],
				totalItems: 0,
			},
			isSuccessful: false,
		});
		this.handleFailure('exception', error);
		this.loggingService.trackException(error);
	}

	handleFailure = (scenario: 'noResults' | 'exception', error: any): void => {
		const queryParams = this.route.snapshot.queryParams;

		this.loggingService.trackEvent(`UI:SearchResults:Any:${scenario}`, {
			...queryParams,
			error,
		});
	};

	rehydrateProductsFromApi(): Observable<ProductSearchResponse> {
		const queryParams = this.searchStoreService.buildQuery();
		const res$ = this.fetchProducts(
			{
				...queryParams,
				target: this.searchStoreService.state.target,
			},
			this.searchStoreService.state.routeUrl
		).pipe(
			take(1),
			catchError((error) => {
				this.handleSearchError(error);
				return of(error);
			})
		);

		res$.subscribe((res: ProductSearchResponse) => {
			// TODO: To be removed with (CHMFP-226)
			this.loggingService.trackEvent(`UI:SearchResults:Any:rehydrateProductsFromApi`, {
				...queryParams,
				res,
			});
			this.searchStoreService.setStateFromServiceResponse(
				res,
				res.products && res.params,
				(res.products && res.routeUrl) || ''
			);
		});

		return res$;
	}

	trackSearchPageView(response: SearchResponse): void {
		const { totalItems } = response.products || {};
		const cppItemCount = (
			response.products?.items?.filter((item) => item.type === 'Product' && !!(<ProductResponse>item).adId) || []
		).length;
		const gridPromotionCount = (
			response.products?.items?.filter(
				(item) => item.type === 'PromoTile' || item.type === 'PromotionalCarousel'
			) || []
		).length;
		const organicItemCount = (
			response.products?.items?.filter((item) => item.type === 'Product' && !(<ProductResponse>item).adId) || []
		).length;
		const navigation = Object.values(this.searchStoreService.breadcrumb)
			.map((item) => item?.name)
			.filter(isDefined);

		this.datalayerService.trackSearchPageView({
			pageContent: totalItems === 0 ? 'No content' : '',
			filters: this.searchStoreService.state.selectedFilters.map(({ name }) => name).filter(isDefined),
			sortBy: this.searchStoreService.state.currentSortOption,
			itemsPerPage: this.searchStoreService.state.currentPageSize,
			navigation: navigation,
			pagination: this.searchStoreService.state.currentPage,
			itemsInResultsSet: totalItems,
			organicItemCount: organicItemCount,
			cppItemCount: cppItemCount,
			gridPromotionCount: gridPromotionCount,
		});
	}

	fetchProductsFromRoute(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot,
		additionalParams?: { [name: string]: any }
	): Observable<ProductSearchResponse> {
		const routeUrl = state.url.split('?') && state.url.split('?')[0];
		const target = route.data['target'];
		const servicePayload: any = {
			dasFilter: <any[]>[],
			target,
			groupId: null,
			dynamicGroup: null,
			search: '',
			boostCampaignId: '',
			...route.queryParams,
			...additionalParams,
		};

		const servicePayloadAdditionalFields: Record<string, unknown> = [
			{
				condition: isRequiredTarget([SearchTarget.ProductGroup], target),
				fields: { groupId: route.params?.groupId },
			},
			{
				condition: isRequiredTarget([SearchTarget.Browse, SearchTarget.Specials], target),
				fields: { dasFilter: this.routeParamsToDASFilters(route.params) },
			},
			{
				condition: isRequiredTarget([SearchTarget.BoostOffer], target),
				fields: { boostCampaignId: route.params?.campaignId },
			},
			{
				condition: isRequiredTarget([SearchTarget.SKUs], target),
				fields: { target: SearchTarget.Browse },
			},
			{
				condition: isBrowseOrSearch(target),
				fields: {
					size: PageSize.FourtyEight,
				},
			},
			{
				condition: isRequiredTarget([SearchTarget.Specials], target),
				fields: {
					useRankedSpecials: true,
				},
			},
		].reduce(
			(additionalFields, { condition, fields }) =>
				condition ? { ...additionalFields, ...fields } : additionalFields,
			{}
		);

		// When feature is enabled, try to use the value in route or then the value in local storage
		const isShowInStockItemsOnlyFeatureEnabled = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.SwitchToggleToShowInStockItemsOnly
		);
		const isHideSwitchToggleForOOSEnabled = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.HideSwitchToggleForOOS
		);

		if (isShowInStockItemsOnlyFeatureEnabled && isBrowseOrSearch(target)) {
			if (isHideSwitchToggleForOOSEnabled) {
				this.localStorageService.setItem('showInStockProductsOnly', 'false');
				servicePayload.inStockProductsOnly = 'false';
			} else if (
				typeof servicePayload.inStockProductsOnly === 'undefined' ||
				servicePayload.inStockProductsOnly === null
			) {
				const showInStockProductsOnlyLocalStorageValue =
					this.localStorageService.getItem('showInStockProductsOnly');

				if (typeof showInStockProductsOnlyLocalStorageValue === 'undefined') {
					this.localStorageService.setItem('showInStockProductsOnly', 'false');
					servicePayload.inStockProductsOnly = 'false';
				} else {
					servicePayload.inStockProductsOnly = String(showInStockProductsOnlyLocalStorageValue);
				}
			}
		}

		if (target === SearchTarget.DynamicProductGroup) {
			// If trying to access the Onecard products, redirect to EDR member products if EDR is enabled
			if (
				route.params.dynamicProductGroup === 'onecard' &&
				this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.EnableEDR)
			) {
				// Redirect to /shop/products/member-prices
				this.router.navigateByUrl('/shop/products/member-prices', { replaceUrl: true });
			}

			if (this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.GreatToLowPrice)) {
				if (route.params.dynamicProductGroup === dynamicProductGroups.greatprice) {
					this.router.navigateByUrl('/shop/products/lowprice', { replaceUrl: true });
				} else if (route.params.dynamicProductGroup === dynamicProductGroups.lowprice) {
					servicePayload.dynamicGroup = (<any>dynamicProductGroups)[dynamicProductGroups.greatprice];
				} else {
					servicePayload.dynamicGroup = (<any>dynamicProductGroups)[route.params.dynamicProductGroup];
				}
			} else {
				servicePayload.dynamicGroup = (<any>dynamicProductGroups)[route.params.dynamicProductGroup];
			}
		}

		this.flagService
			.getVariationKey(FlagKey.infiniteLoadMore)
			.pipe(
				take(1),
				filter((variantKey) => InfiniteLoadMoreExperimentVariants.InfiniteList === variantKey)
			)
			.subscribe(() => {
				servicePayloadAdditionalFields.size = PageSize.TwentyFour;
			});

		const finalServicePayload = { ...servicePayload, ...servicePayloadAdditionalFields };

		this.loggingService.trackEvent('UI:Search:Any:resolve', finalServicePayload);
		this.searchStoreService.setQueryParamsState(finalServicePayload);
		return this.fetchProducts(finalServicePayload, routeUrl);
	}

	private routeParamsToDASFilters = (params = {}): (string | null)[] =>
		Object.keys(params).map(
			(k) =>
				k &&
				this.searchStoreService.buildFilterQueryString(<FilterCriteria>{
					key: sentenceCase(k),
					value: '', // This is intentional
					name: (<any>params)[k],
					isBooleanValue: false,
				})
		);
}
